import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Reorder } from 'framer-motion'
import { ArrowLeft, Image, X, GripVertical } from 'lucide-react'
import {
  useCreateEvent,
  useUpdateEvent,
  useEvent,
  useEventLineup,
  useUpsertEventLineup,
  useEventSetlist,
  useUpsertEventSetlist,
} from '@/hooks/use-events'
import { useMembers } from '@/hooks/use-members'
import { useAllMusicTracks, useCoverSongs } from '@/hooks/use-music'
import MediaPicker from '@/components/media/media-picker'
import provincesJson from '@/data/geo/provinsi/provinsi.json'

type Tab = 'detail' | 'lineup' | 'setlist' | 'pengaturan'

type WilayahEntry = { id: string; name: string }

const PROVINCES: WilayahEntry[] = Object.entries(
  provincesJson as Record<string, string>
).map(([id, name]) => ({ id, name }))

const kabupatenModules = import.meta.glob<{ default: Record<string, string> }>(
  '../../data/geo/kabupaten_kota/*.json'
)

interface FormState {
  title: string
  is_all_day: boolean
  starts_at: string
  ends_at: string
  description: string
  location: string
  province: string
  city: string
  eo_link: string
}

const EMPTY_FORM: FormState = {
  title: '',
  is_all_day: false,
  starts_at: '',
  ends_at: '',
  description: '',
  location: '',
  province: '',
  city: '',
  eo_link: '',
}

type SetlistEntry = { song_type: 'track' | 'cover'; song_id: string }

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.slice(0, 16)
}

function toDateLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.slice(0, 10)
}

export default function EventFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: event, isLoading: eventLoading } = useEvent(id)
  const { data: existingLineup = [] } = useEventLineup(id)
  const { data: existingSetlist = [] } = useEventSetlist(id)
  const { data: allMembers = [] } = useMembers()
  const { data: allTracks = [] } = useAllMusicTracks()
  const { data: coverSongs = [] } = useCoverSongs()
  const activeMembers = allMembers.filter((m) => m.is_active)

  const createEvent = useCreateEvent()
  const updateEvent = useUpdateEvent()
  const upsertLineup = useUpsertEventLineup()
  const upsertSetlist = useUpsertEventSetlist()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [provinceCode, setProvinceCode] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [lineupIds, setLineupIds] = useState<string[]>([])
  const [selectedSetlist, setSelectedSetlist] = useState<SetlistEntry[]>([])
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [coverAssetId, setCoverAssetId] = useState<string | null>(null)
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('detail')

  const { data: regencies, isLoading: isLoadingRegencies } = useQuery({
    queryKey: ['wilayah-regencies', provinceCode],
    enabled: !!provinceCode,
    staleTime: Infinity,
    queryFn: async (): Promise<WilayahEntry[]> => {
      const key = `../../data/geo/kabupaten_kota/kab-${provinceCode}.json`
      const loader = kabupatenModules[key]
      if (!loader) return []
      const mod = await loader()
      return Object.entries(mod.default).map(([subCode, name]) => ({
        id: `${provinceCode}.${subCode}`,
        name,
      }))
    },
  })

  // Populate form when event data loads (edit mode)
  useEffect(() => {
    if (event) {
      const meta = (event.metadata as Record<string, unknown> | null) ?? {}
      const isAllDay = meta.is_all_day === true
      const savedProvince = event.province ?? ''
      setForm({
        title: event.title,
        is_all_day: isAllDay,
        starts_at: isAllDay ? toDateLocal(event.starts_at) : toDatetimeLocal(event.starts_at),
        ends_at: isAllDay ? toDateLocal(event.ends_at) : toDatetimeLocal(event.ends_at),
        description: event.description ?? '',
        location: event.location ?? '',
        province: savedProvince,
        city: event.city ?? '',
        eo_link: event.online_url ?? '',
      })
      setProvinceCode(PROVINCES.find((p) => p.name === savedProvince)?.id ?? '')
      setIsPublished(event.is_published)
      setCoverUrl(event.cover_url ?? null)
      setCoverAssetId(event.asset_id ?? null)
    }
  }, [event?.id])

  // Sync lineup when data loads (edit mode)
  useEffect(() => {
    if (isEdit) {
      setLineupIds(existingLineup.map((m) => m.id))
    }
  }, [existingLineup, isEdit])

  // Sync setlist when data loads (edit mode)
  useEffect(() => {
    if (isEdit) {
      setSelectedSetlist(
        existingSetlist.map((s) => ({
          song_type: s.song_type as 'track' | 'cover',
          song_id: s.song_type === 'track' ? s.track_id! : s.cover_id!,
        }))
      )
    }
  }, [existingSetlist, isEdit])

  const isPending =
    createEvent.isPending ||
    updateEvent.isPending ||
    upsertLineup.isPending ||
    upsertSetlist.isPending
  const mutationError =
    createEvent.error ?? updateEvent.error ?? upsertLineup.error ?? upsertSetlist.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleAllDayToggle(checked: boolean) {
    setForm((f) => ({ ...f, is_all_day: checked, starts_at: '', ends_at: '' }))
  }

  function handleSave() {
    if (!form.title.trim() || !form.starts_at) return

    const existingMeta = (event?.metadata as Record<string, unknown> | null) ?? {}
    const payload = {
      title: form.title.trim(),
      event_type: 'live_show' as const,
      starts_at: form.is_all_day
        ? new Date(form.starts_at + 'T00:00:00').toISOString()
        : new Date(form.starts_at).toISOString(),
      ends_at: form.ends_at
        ? form.is_all_day
          ? new Date(form.ends_at + 'T23:59:59').toISOString()
          : new Date(form.ends_at).toISOString()
        : null,
      description: form.description.trim() || null,
      location: form.location.trim() || null,
      province: form.province.trim() || null,
      city: form.city.trim() || null,
      online_url: form.eo_link.trim() || null,
      cover_url: coverUrl,
      asset_id: coverAssetId,
      is_published: isPublished,
      metadata: { ...existingMeta, is_all_day: form.is_all_day },
    }

    const setlistPayload = selectedSetlist.map((entry, idx) => ({
      song_type: entry.song_type,
      track_id: entry.song_type === 'track' ? entry.song_id : null,
      cover_id: entry.song_type === 'cover' ? entry.song_id : null,
      order_num: idx,
    }))

    function saveSetlistThenNavigate(eventId: string) {
      upsertSetlist.mutate(
        { eventId, items: setlistPayload },
        { onSuccess: () => navigate('/events') },
      )
    }

    if (isEdit && id) {
      updateEvent.mutate(
        { id, ...payload },
        {
          onSuccess: () =>
            upsertLineup.mutate(
              { eventId: id, memberIds: lineupIds },
              { onSuccess: () => saveSetlistThenNavigate(id) },
            ),
        },
      )
    } else {
      createEvent.mutate(payload, {
        onSuccess: (newEvent) =>
          upsertLineup.mutate(
            { eventId: newEvent.id, memberIds: lineupIds },
            { onSuccess: () => saveSetlistThenNavigate(newEvent.id) },
          ),
      })
    }
  }

  if (isEdit && eventLoading) {
    return <p className="text-muted-foreground">Memuat data event...</p>
  }

  return (
    <>
      <div className="max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/events')}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{isEdit ? 'Edit Event' : 'Tambah Event'}</h1>
            <p className="text-muted-foreground text-sm">
              {isEdit ? 'Perbarui informasi event.' : 'Isi detail event baru.'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {([
            { key: 'detail',      label: 'Detail' },
            { key: 'lineup',      label: 'Lineup' },
            { key: 'setlist',     label: 'Setlist' },
            { key: 'pengaturan',  label: 'Pengaturan' },
          ] as { key: Tab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab: Detail */}
        {activeTab === 'detail' && (
          <div className="space-y-6">
            {/* Thumbnail */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Thumbnail</h2>
              {coverUrl ? (
                <div className="relative w-full h-40 rounded-lg overflow-hidden bg-muted">
                  <img src={coverUrl} alt="cover preview" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => { setCoverUrl(null); setCoverAssetId(null) }}
                    className="absolute top-2 right-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setMediaPickerOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border h-28 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Image className="h-4 w-4" />
                  Pilih dari Media
                </button>
              )}
              {coverUrl && (
                <button
                  type="button"
                  onClick={() => setMediaPickerOpen(true)}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors"
                >
                  <Image className="h-3.5 w-3.5" />
                  Ganti Thumbnail
                </button>
              )}
            </div>

            {/* Info Utama */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Info Utama</h2>
              <div className="space-y-1">
                <label className="text-sm font-medium">Judul *</label>
                <input
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Nama event"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.is_all_day}
                  onChange={(e) => handleAllDayToggle(e.target.checked)}
                  className="rounded border-input accent-primary"
                />
                <span className="text-sm">Sepanjang hari (tanpa waktu spesifik)</span>
              </label>

              {form.is_all_day ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Tanggal *</label>
                    <input
                      type="date"
                      value={form.starts_at}
                      onChange={(e) => set('starts_at', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Sampai</label>
                    <input
                      type="date"
                      value={form.ends_at}
                      onChange={(e) => set('ends_at', e.target.value)}
                      min={form.starts_at}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Mulai *</label>
                    <input
                      type="datetime-local"
                      value={form.starts_at}
                      onChange={(e) => set('starts_at', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Selesai</label>
                    <input
                      type="datetime-local"
                      value={form.ends_at}
                      onChange={(e) => set('ends_at', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Lokasi & Tiket */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Lokasi & Tiket</h2>

              {/* Detail Lokasi */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Detail Lokasi</label>
                <input
                  value={form.location}
                  onChange={(e) => set('location', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Nama venue / alamat lengkap (opsional)"
                />
              </div>

              {/* Provinsi */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Provinsi</label>
                <select
                  value={provinceCode}
                  onChange={(e) => {
                    const found = PROVINCES.find((p) => p.id === e.target.value)
                    setProvinceCode(e.target.value)
                    set('province', found?.name ?? '')
                    set('city', '')
                  }}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Pilih provinsi</option>
                  {PROVINCES.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Kabupaten / Kota */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Kabupaten / Kota</label>
                <select
                  value={form.city}
                  disabled={!provinceCode || isLoadingRegencies}
                  onChange={(e) => set('city', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value="">
                    {!provinceCode
                      ? 'Pilih provinsi terlebih dahulu'
                      : isLoadingRegencies
                        ? 'Memuat...'
                        : 'Pilih kabupaten / kota'}
                  </option>
                  {regencies?.map((r) => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))}
                </select>
              </div>

              {/* Link EO / Tiket */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Link EO / Tiket</label>
                <input
                  type="url"
                  value={form.eo_link}
                  onChange={(e) => set('eo_link', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://loket.com/event/... (opsional)"
                />
              </div>
            </div>

            {/* Deskripsi */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Deskripsi</h2>
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Deskripsi singkat event (opsional)"
              />
            </div>
          </div>
        )}

        {/* Tab: Lineup */}
        {activeTab === 'lineup' && (
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div>
              <h2 className="font-semibold">Lineup Member</h2>
              <p className="text-sm text-muted-foreground">
                Kosongkan jika semua member aktif tampil di event ini.
              </p>
            </div>
            {activeMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada member aktif.</p>
            ) : (
              <div className="space-y-2">
                {activeMembers.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lineupIds.includes(m.id)}
                      onChange={(e) =>
                        setLineupIds((prev) =>
                          e.target.checked
                            ? [...prev, m.id]
                            : prev.filter((lid) => lid !== m.id)
                        )
                      }
                      className="accent-primary"
                    />
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full overflow-hidden bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground shrink-0">
                        {m.photo_url ? (
                          <img src={m.photo_url} alt={m.stage_name ?? m.name} className="h-full w-full object-cover" />
                        ) : (
                          <span>{(m.stage_name ?? m.name).charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <span className="text-sm">{m.stage_name ?? m.name}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {lineupIds.length > 0 && (
              <button
                type="button"
                onClick={() => setLineupIds([])}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Reset (pilih semua)
              </button>
            )}
          </div>
        )}

        {/* Tab: Setlist */}
        {activeTab === 'setlist' && (
          <div className="space-y-6">
            {/* Ordered list */}
            {selectedSetlist.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-6 space-y-3">
                <div>
                  <h2 className="font-semibold">Urutan Setlist</h2>
                  <p className="text-sm text-muted-foreground">Drag untuk mengubah urutan.</p>
                </div>
                <Reorder.Group
                  axis="y"
                  values={selectedSetlist}
                  onReorder={setSelectedSetlist}
                  className="space-y-1"
                >
                  {selectedSetlist.map((entry, idx) => {
                    const track =
                      entry.song_type === 'track'
                        ? allTracks.find((t) => t.id === entry.song_id)
                        : null
                    const cover =
                      entry.song_type === 'cover'
                        ? coverSongs.find((c) => c.id === entry.song_id)
                        : null
                    const title = track?.title ?? cover?.title ?? '?'
                    const subtitle =
                      track?.music_releases?.title ?? cover?.original_artist ?? ''
                    return (
                      <Reorder.Item
                        key={`${entry.song_type}-${entry.song_id}`}
                        value={entry}
                        className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm cursor-grab active:cursor-grabbing select-none"
                      >
                        <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                        <span className="text-xs text-muted-foreground w-5 text-right shrink-0">
                          {idx + 1}.
                        </span>
                        <span className="flex-1 font-medium truncate">{title}</span>
                        {subtitle && (
                          <span className="text-xs text-muted-foreground truncate hidden sm:block">
                            — {subtitle}
                          </span>
                        )}
                        <span
                          className={`text-xs rounded px-1.5 py-0.5 shrink-0 ${
                            entry.song_type === 'track'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {entry.song_type === 'track' ? 'Diskografi' : 'Bawaan'}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedSetlist((prev) =>
                              prev.filter(
                                (x) =>
                                  !(
                                    x.song_type === entry.song_type &&
                                    x.song_id === entry.song_id
                                  )
                              )
                            )
                          }
                          className="rounded p-0.5 text-muted-foreground hover:text-destructive shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Reorder.Item>
                    )
                  })}
                </Reorder.Group>
              </div>
            )}

            {/* Song picker */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">
                {selectedSetlist.length > 0 ? 'Tambah Lagu' : 'Pilih Lagu'}
              </h2>

              {allTracks.length === 0 && coverSongs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Belum ada lagu. Tambahkan musik di halaman Musik terlebih dahulu.
                </p>
              ) : (
                <>
                  {allTracks.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Diskografi
                      </p>
                      <div className="space-y-1.5">
                        {allTracks.map((track) => {
                          const isChecked = selectedSetlist.some(
                            (e) => e.song_type === 'track' && e.song_id === track.id
                          )
                          return (
                            <label key={track.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) =>
                                  setSelectedSetlist((prev) =>
                                    e.target.checked
                                      ? [...prev, { song_type: 'track', song_id: track.id }]
                                      : prev.filter(
                                          (x) =>
                                            !(x.song_type === 'track' && x.song_id === track.id)
                                        )
                                  )
                                }
                                className="accent-primary"
                              />
                              <span className="text-sm">{track.title}</span>
                              {track.music_releases && (
                                <span className="text-xs text-muted-foreground">
                                  — {track.music_releases.title}
                                </span>
                              )}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {coverSongs.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Lagu Bawaan
                      </p>
                      <div className="space-y-1.5">
                        {coverSongs.map((song) => {
                          const isChecked = selectedSetlist.some(
                            (e) => e.song_type === 'cover' && e.song_id === song.id
                          )
                          return (
                            <label key={song.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) =>
                                  setSelectedSetlist((prev) =>
                                    e.target.checked
                                      ? [...prev, { song_type: 'cover', song_id: song.id }]
                                      : prev.filter(
                                          (x) =>
                                            !(x.song_type === 'cover' && x.song_id === song.id)
                                        )
                                  )
                                }
                                className="accent-primary"
                              />
                              <span className="text-sm">{song.title}</span>
                              <span className="text-xs text-muted-foreground">
                                — {song.original_artist}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Tab: Pengaturan */}
        {activeTab === 'pengaturan' && (
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h2 className="font-semibold">Pengaturan Publikasi</h2>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="rounded border-input accent-primary"
              />
              <div>
                <p className="text-sm font-medium">Publikasikan langsung</p>
                <p className="text-xs text-muted-foreground">
                  Event akan terlihat di halaman publik setelah disimpan.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={isPending || !form.title.trim() || !form.starts_at}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Event'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/events')}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
          >
            Batal
          </button>
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan.'}
            </p>
          )}
        </div>
      </div>

      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={(assetId, url) => {
          setCoverAssetId(assetId)
          setCoverUrl(url)
          setMediaPickerOpen(false)
        }}
      />
    </>
  )
}
